// Input validation and sanitization utilities

export const sanitizeInput = (input: string): string => {
  return input
    .trim()
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .substring(0, 1000); // Limit length
};

export const sanitizeEmail = (email: string): string => {
  return email
    .trim()
    .toLowerCase()
    .substring(0, 254); // RFC 5321 limit
};

export const validateEmail = (email: string): { isValid: boolean; error?: string } => {
  const sanitized = sanitizeEmail(email);
  
  if (!sanitized) {
    return { isValid: false, error: 'Email is required' };
  }

  // RFC 5322 compliant email regex (simplified but robust)
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  
  if (!emailRegex.test(sanitized)) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }

  // Check for common disposable email domains
  const disposableDomains = [
    '10minutemail.com',
    'tempmail.org',
    'guerrillamail.com',
    'mailinator.com',
    'temp-mail.org'
  ];

  const domain = sanitized.split('@')[1];
  if (disposableDomains.includes(domain)) {
    return { isValid: false, error: 'Please use a permanent email address' };
  }

  return { isValid: true };
};

export const validateName = (name: string): { isValid: boolean; error?: string } => {
  const sanitized = sanitizeInput(name);
  
  if (sanitized.length > 100) {
    return { isValid: false, error: 'Name must be less than 100 characters' };
  }

  // Allow letters, spaces, hyphens, apostrophes, and dots
  const nameRegex = /^[a-zA-Z\s\-'.]+$/;
  
  if (sanitized && !nameRegex.test(sanitized)) {
    return { isValid: false, error: 'Name contains invalid characters' };
  }

  return { isValid: true };
};

export const validateRole = (role: string): { isValid: boolean; error?: string } => {
  const sanitized = sanitizeInput(role);
  
  if (sanitized.length > 100) {
    return { isValid: false, error: 'Role must be less than 100 characters' };
  }

  return { isValid: true };
};

export const validateFormData = (data: {
  email: string;
  name?: string;
  role?: string;
}): { isValid: boolean; errors: Record<string, string> } => {
  const errors: Record<string, string> = {};

  const emailValidation = validateEmail(data.email);
  if (!emailValidation.isValid) {
    errors.email = emailValidation.error!;
  }

  if (data.name) {
    const nameValidation = validateName(data.name);
    if (!nameValidation.isValid) {
      errors.name = nameValidation.error!;
    }
  }

  if (data.role) {
    const roleValidation = validateRole(data.role);
    if (!roleValidation.isValid) {
      errors.role = roleValidation.error!;
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Rate limiting utilities
const submissionTimes: number[] = [];

export const checkRateLimit = (maxSubmissions: number = 3, timeWindow: number = 60000): boolean => {
  const now = Date.now();
  
  // Remove old submissions outside the time window
  while (submissionTimes.length > 0 && submissionTimes[0] < now - timeWindow) {
    submissionTimes.shift();
  }

  // Check if user has exceeded rate limit
  if (submissionTimes.length >= maxSubmissions) {
    return false;
  }

  // Add current submission
  submissionTimes.push(now);
  return true;
};

export const getRateLimitStatus = (): { 
  canSubmit: boolean; 
  remainingSubmissions: number; 
  resetTime?: number 
} => {
  const maxSubmissions = 3;
  const timeWindow = 60000; // 1 minute
  const now = Date.now();

  // Clean old submissions
  while (submissionTimes.length > 0 && submissionTimes[0] < now - timeWindow) {
    submissionTimes.shift();
  }

  const remainingSubmissions = Math.max(0, maxSubmissions - submissionTimes.length);
  const canSubmit = remainingSubmissions > 0;
  const resetTime = submissionTimes.length > 0 ? submissionTimes[0] + timeWindow : undefined;

  return {
    canSubmit,
    remainingSubmissions,
    resetTime
  };
};

// Honeypot validation (bot detection)
export const validateHoneypot = (honeypotValue: string): boolean => {
  // Honeypot should always be empty for human users
  return honeypotValue === '' || honeypotValue === undefined;
};

// CSRF-like timestamp validation
export const validateTimestamp = (timestamp: string, maxAge: number = 300000): boolean => {
  try {
    const submissionTime = new Date(timestamp).getTime();
    const now = Date.now();
    const age = now - submissionTime;
    
    // Form must not be too old (max 5 minutes) and not from the future
    return age >= 0 && age <= maxAge;
  } catch {
    return false;
  }
};